import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AlertPriority, AlertEventName, WebSocketAlert } from '../lib/alertTypes';

export interface OverlayAlert {
    id: string;
    eventType: AlertEventName;
    title: string;
    message: string;
    priority: AlertPriority;
    timestamp: Date;
    action?: {
        label: string;
        url: string;
    };
    rawData: WebSocketAlert;
}

interface AlertContextType {
    activeAlerts: OverlayAlert[];
    showAlert: (alert: Omit<OverlayAlert, 'id' | 'timestamp'>) => void;
    dismissAlert: (id: string) => void;
    dismissAllAlerts: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlerts = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlerts must be used within AlertProvider');
    }
    return context;
};

interface AlertProviderProps {
    children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
    const [activeAlerts, setActiveAlerts] = useState<OverlayAlert[]>([]);

    const showAlert = useCallback((alert: Omit<OverlayAlert, 'id' | 'timestamp'>) => {
        const newAlert: OverlayAlert = {
            ...alert,
            id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
        };

        setActiveAlerts(prev => [...prev, newAlert]);

        // Auto-dismiss based on priority
        const autoDismissTime = alert.priority === 'critical' ? 15000 :
            alert.priority === 'high' ? 10000 :
                alert.priority === 'normal' ? 6000 : 4000;

        setTimeout(() => {
            setActiveAlerts(prev => prev.filter(a => a.id !== newAlert.id));
        }, autoDismissTime);
    }, []);

    const dismissAlert = useCallback((id: string) => {
        setActiveAlerts(prev => prev.filter(a => a.id !== id));
    }, []);

    const dismissAllAlerts = useCallback(() => {
        setActiveAlerts([]);
    }, []);

    return (
        <AlertContext.Provider value={{ activeAlerts, showAlert, dismissAlert, dismissAllAlerts }}>
            {children}
        </AlertContext.Provider>
    );
};
